import React from "react";
import { useTranslation } from "react-i18next";
import { IoClose } from "react-icons/io5";
import {
  FiBox,
  FiEdit2,
  FiImage,
  FiRefreshCw,
  FiTrash2,
  FiUploadCloud,
} from "react-icons/fi";
import { Tooltip } from "flowbite-react";
import { invalidate } from "@react-three/fiber";
import { observer } from "mobx-react-lite";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { useMutation, useQuery } from "@apollo/client";
import { floorplannerStore } from "../../store/floorplannerStore";
import { userStore } from "../../store/userStore";
import {
  DeleteUserModel3DDocument,
  RetryUserModel3DGenerationDocument,
  UploadUserModel3DThumbnailDocument,
  UserModels3DDocument,
} from "../../gql/userModel3D";
import { markUserModel3DUnavailable } from "../../features/floorplan3d/symbols/assets/symbol3DAssetAvailability";
import { insertUserModel3D } from "../../features/floorplan3d/userModels/insertUserModel3D";
import {
  getUserModel3DTitle,
  UserModel3DLibraryItem,
} from "../../features/floorplan3d/userModels/userModel3DTypes";
import {
  emptyUserModel3DCommerceForm,
  parseCommerceFormForCreate,
  UserModel3DCommerceFormState,
  UserModel3DCommerceMutationVars,
} from "../../features/floorplan3d/userModels/userModel3DCommerce";
import {
  commerceStampFromLibraryItem,
  syncUserModel3DCommerceOnPlacements,
} from "../../features/floorplan3d/userModels/userModel3DCommerceStamp";
import { resolveUserModel3DUrl } from "../../features/floorplan3d/userModels/userModel3DUrls";
import { BONI_CHECKBOX_CLASSNAME } from "../../constants/brandColors";
import { CreateModel3DFromImageModal } from "./CreateModel3DFromImageModal";
import { EditUserModel3DModal } from "./EditUserModel3DModal";
import { UserModel3DCommerceFields } from "./UserModel3DCommerceFields";

export type { UserModel3DLibraryItem } from "../../features/floorplan3d/userModels/userModel3DTypes";
export { getUserModel3DInsertDimensions } from "../../features/floorplan3d/userModels/insertUserModel3D";

type UserModels3DPanelMode = "insert" | "select";

interface DropdownProps {
  position?: { top: number; left: number };
  onClose: () => void;
  dropdownRef?: React.Ref<HTMLDivElement>;
  mode?: UserModels3DPanelMode;
  onModelSelect?: (model: UserModel3DLibraryItem) => void;
  selectedModelId?: string;
}

type UserModel3DUploadProgress = {
  fileName: string;
  loaded: number;
  total: number;
  phase: "uploading" | "cloud" | "saving" | "thumbnail";
};

const PANEL_WIDTH = 398;
const PANEL_MIN_VISIBLE_HEIGHT = 160;
const PANEL_VIEWPORT_GAP = 12;
const PANEL_DEFAULT_TOP = 96;
const PANEL_DEFAULT_RIGHT = 360;

const getViewportSize = () => ({
  width: window.innerWidth,
  height: window.innerHeight,
});

const isSupportedModelFile = (file: File) =>
  /\.(glb|obj|zip|rar)$/i.test(file.name.toLowerCase());

const getModelBaseName = (fileName: string) =>
  fileName.replace(/\.(glb|obj|zip|rar)$/i, "").trim() || "3D object";

const formatBytes = (bytes: number) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / 1024 ** exponent;
  const formatted =
    value >= 10 || exponent === 0 ? Math.round(value) : value.toFixed(1);
  return `${formatted} ${units[exponent]}`;
};

const getReadableUploadError = (error: unknown) => {
  const message =
    error instanceof Error ? error.message : "Could not upload 3D object.";
  const rawLimitMatch = message.match(
    /File truncated as it exceeds the (\d+) byte size limit/i,
  );
  if (rawLimitMatch) {
    return `This file is larger than the ${formatBytes(
      Number(rawLimitMatch[1]),
    )} upload limit.`;
  }
  return message.replace(
    /(\d+) byte size limit/gi,
    (_, bytes) => `${formatBytes(Number(bytes))} size limit`,
  );
};

const getReadableProcessingError = (message?: string | null) => {
  if (!message) return "Could not process this object.";
  if (/No value provided for input HTTP label:\s*Key/i.test(message)) {
    return "BONI could not retrieve the 3D result. Please retry.";
  }
  const budgetMatch = message.match(
    /(?:^|\s)(?:\S+optimized\.glb|Optimized model|Optimized object) is (\d+) bytes, over max (\d+) bytes/i,
  );
  if (budgetMatch) {
    return `Optimized object is ${formatBytes(
      Number(budgetMatch[1]),
    )}, over the ${formatBytes(Number(budgetMatch[2]))} processing limit.`;
  }
  return message.replace(/\/var\/folders\/\S+/g, "Temporary processing file");
};

const USER_MODEL_3D_UPLOAD_MUTATION = `
  mutation UploadUserModel3D(
    $name: String
    $model: Upload!
    $shareWithCommunity: Boolean
    $price: Float
    $currency: String
    $url: String
    $brand: String
  ) {
    uploadUserModel3D(
      name: $name
      model: $model
      shareWithCommunity: $shareWithCommunity
      price: $price
      currency: $currency
      url: $url
      brand: $brand
    ) {
      id
      name
      displayTitle
      displayDescription
      uploaderId
      uploaderName
      originalFilename
      status
      errorMessage
      shareWithCommunity
      isCommunityListed
      communityListingState
      firstSharedAt
      ownerArchivedAt
      moderationState
      optimizedUrl
      thumbnailUrl
      price
      currency
      url
      brand
      dimensions {
        width
        depth
        height
      }
      bounds {
        min
        max
        center
        size {
          width
          depth
          height
        }
      }
      fileBytes
      triangleCount
      vertexCount
      textureMaxSize
      sha256
      pipelineVersion
      createdAt
      updatedAt
      architectureSymbolKey
    }
  }
`;

const uploadUserModel3DWithProgress = ({
  file,
  name,
  shareWithCommunity,
  commerce,
  onProgress,
}: {
  file: File;
  name: string;
  shareWithCommunity: boolean;
  commerce?: UserModel3DCommerceMutationVars;
  onProgress: (progress: UserModel3DUploadProgress) => void;
}) =>
  new Promise<UserModel3DLibraryItem>((resolve, reject) => {
    const endpoint = process.env.REACT_APP_SERVER_URI;
    if (!endpoint) {
      reject(new Error("Server URL is not configured."));
      return;
    }

    const form = new FormData();
    form.append(
      "operations",
      JSON.stringify({
        query: USER_MODEL_3D_UPLOAD_MUTATION,
        variables: {
          name,
          model: null,
          shareWithCommunity,
          price: commerce?.price ?? null,
          currency: commerce?.currency ?? null,
          url: commerce?.url ?? null,
          brand: commerce?.brand ?? null,
        },
      }),
    );
    form.append("map", JSON.stringify({ "0": ["variables.model"] }));
    form.append("0", file);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", endpoint);
    xhr.withCredentials = true;
    xhr.setRequestHeader("Apollo-Require-Preflight", "true");

    xhr.upload.onprogress = (event) => {
      const total = event.lengthComputable ? event.total : file.size;
      const loaded = event.loaded;
      onProgress({
        fileName: file.name,
        loaded,
        total,
        phase: total > 0 && loaded >= total ? "cloud" : "uploading",
      });
    };

    xhr.onload = () => {
      let response: any;
      try {
        response = JSON.parse(xhr.responseText);
      } catch (error) {
        reject(new Error("Could not read the upload response."));
        return;
      }

      if (xhr.status < 200 || xhr.status >= 300 || response.errors?.length) {
        reject(
          new Error(
            response.errors?.[0]?.message ||
              `Upload failed with status ${xhr.status}.`,
          ),
        );
        return;
      }

      const uploadedModel = response.data?.uploadUserModel3D;
      if (!uploadedModel?.id) {
        reject(new Error("Upload completed but no object was returned."));
        return;
      }
      onProgress({
        fileName: file.name,
        loaded: file.size,
        total: file.size,
        phase: "saving",
      });
      resolve(uploadedModel as UserModel3DLibraryItem);
    };

    xhr.onerror = () =>
      reject(new Error("Network error while uploading object."));
    xhr.onabort = () => reject(new Error("Object upload was cancelled."));
    xhr.send(form);

    onProgress({
      fileName: file.name,
      loaded: 0,
      total: file.size,
      phase: "uploading",
    });
  });

const renderUserModelThumbnail = async (file: File): Promise<File> => {
  const objectUrl = URL.createObjectURL(file);
  const renderer = new THREE.WebGLRenderer({
    alpha: true,
    antialias: true,
    preserveDrawingBuffer: true,
  });
  renderer.setSize(512, 512);
  renderer.setPixelRatio(1);
  renderer.setClearColor(0xffffff, 0);

  try {
    const loader = new GLTFLoader();
    const gltf = await loader.loadAsync(objectUrl);
    const scene = new THREE.Scene();
    scene.add(new THREE.AmbientLight(0xffffff, 1.8));
    const key = new THREE.DirectionalLight(0xffffff, 2.4);
    key.position.set(3, 4, 5);
    scene.add(key);

    const model = gltf.scene;
    model.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = false;
        child.receiveShadow = false;
      }
    });
    scene.add(model);

    const box = new THREE.Box3().setFromObject(model);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const maxDimension = Math.max(size.x, size.y, size.z, 0.1);
    model.position.sub(center);

    const camera = new THREE.PerspectiveCamera(35, 1, 0.01, 1000);
    camera.position.set(
      maxDimension * 1.4,
      -maxDimension * 1.8,
      maxDimension * 1.2,
    );
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();

    renderer.render(scene, camera);
    const canvas = renderer.domElement;
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((nextBlob) => {
        if (nextBlob) resolve(nextBlob);
        else reject(new Error("Could not render object thumbnail"));
      }, "image/png");
    });
    return new File([blob], `user_model_3d_${Date.now()}.png`, {
      type: "image/png",
    });
  } finally {
    renderer.dispose();
    URL.revokeObjectURL(objectUrl);
  }
};

const clearDeletedUserModelReferences = (modelId: string) => {
  const affectedSymbols = Array.from(
    floorplannerStore.symbolsMap.values(),
  ).filter((symbol) => symbol.threeD?.userModel?.modelId === modelId);
  if (affectedSymbols.length === 0) return;

  floorplannerStore.beginUndoTransaction("Remove deleted uploaded 3D object");
  affectedSymbols.forEach((symbol) => {
    floorplannerStore.recordSymbolForUndo(symbol.id);
    floorplannerStore.updateSymbolProperty(symbol.id, "threeD", {
      ...symbol.threeD,
      sourceMode: symbol.threeD?.product ? "custom" : undefined,
      userModel: undefined,
    });
    floorplannerStore.recordSymbolChange(symbol.id, "modify");
  });
  floorplannerStore.pushToUndoStack();
  invalidate();
};

export const UserModels3DPanel: React.FC<DropdownProps> = observer(
  ({
    position,
    onClose,
    dropdownRef,
    mode = "insert",
    onModelSelect,
    selectedModelId,
  }) => {
    const { t } = useTranslation();
    const [viewportSize, setViewportSize] = React.useState(getViewportSize);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
    const [shareWithCommunity, setShareWithCommunity] = React.useState(true);
    const [commerceForm, setCommerceForm] =
      React.useState<UserModel3DCommerceFormState>(() =>
        emptyUserModel3DCommerceForm(floorplannerStore.currency),
      );
    const [uploadingModel, setUploadingModel] = React.useState(false);
    const [uploadProgress, setUploadProgress] =
      React.useState<UserModel3DUploadProgress | null>(null);
    const [modelError, setModelError] = React.useState<string | null>(null);
    const [createFromImageOpen, setCreateFromImageOpen] = React.useState(false);
    const [editingModel, setEditingModel] =
      React.useState<UserModel3DLibraryItem | null>(null);
    const [retryingModelId, setRetryingModelId] = React.useState<string | null>(
      null,
    );
    const userModelsQuery = useQuery(UserModels3DDocument, {
      pollInterval: 5000,
      fetchPolicy: "cache-and-network",
    });
    const [uploadUserModel3DThumbnail] = useMutation(
      UploadUserModel3DThumbnailDocument,
    );
    const [deleteUserModel3D] = useMutation(DeleteUserModel3DDocument);
    const [retryUserModel3DGeneration] = useMutation(
      RetryUserModel3DGenerationDocument,
    );
    const userModels = (userModelsQuery.data?.userModels3D ??
      []) as UserModel3DLibraryItem[];

    const plan = userStore.entitlements?.plan?.toUpperCase() ?? "FREE";
    const canChoosePrivacy = [
      "PRO",
      "PRO_YEAR",
      "ENTERPRISE",
      "DEVELOPER",
    ].includes(plan);

    const chooseFile = (file: File) => {
      setModelError(null);
      if (!isSupportedModelFile(file)) {
        setModelError(t("userModels3d.uploadFileTypesError"));
        return;
      }
      setShareWithCommunity(true);
      setCommerceForm(emptyUserModel3DCommerceForm(floorplannerStore.currency));
      setSelectedFile(file);
    };

    const uploadModel = async (file: File) => {
      setModelError(null);
      let commerce: UserModel3DCommerceMutationVars;
      try {
        commerce = parseCommerceFormForCreate(commerceForm);
      } catch (parseError) {
        setModelError(
          parseError instanceof Error
            ? parseError.message
            : t("userModels3d.invalidPrice"),
        );
        return;
      }
      setUploadingModel(true);
      try {
        const uploadedModel = await uploadUserModel3DWithProgress({
          file,
          name: getModelBaseName(file.name),
          shareWithCommunity: canChoosePrivacy ? shareWithCommunity : true,
          commerce,
          onProgress: setUploadProgress,
        });
        const modelId = uploadedModel.id;
        userModelsQuery.refetch();

        if (modelId) {
          try {
            if (file.name.toLowerCase().endsWith(".glb")) {
              setUploadProgress({
                fileName: file.name,
                loaded: file.size,
                total: file.size,
                phase: "thumbnail",
              });
              const thumbnail = await renderUserModelThumbnail(file);
              await uploadUserModel3DThumbnail({
                variables: { modelId, image: thumbnail },
              });
              userModelsQuery.refetch();
            }
          } catch (thumbnailError) {
            console.warn("Could not render 3D object thumbnail", thumbnailError);
          }
        }
      } catch (error) {
        setModelError(getReadableUploadError(error));
      } finally {
        setUploadingModel(false);
        setUploadProgress(null);
        setSelectedFile(null);
        setCommerceForm(emptyUserModel3DCommerceForm(floorplannerStore.currency));
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    };

    const selectUserModel = (model: UserModel3DLibraryItem) => {
      if (model.status !== "ready" || !model.optimizedUrl) return;
      onModelSelect?.(model);
      onClose();
    };

    const handleReadyModel = (model: UserModel3DLibraryItem) => {
      if (mode === "select") {
        selectUserModel(model);
        return;
      }
      const result = insertUserModel3D(model);
      if (!result.ok) {
        setModelError(result.reason);
        return;
      }
      onClose();
    };

    const deleteUserModel = async (
      event: React.MouseEvent,
      model: UserModel3DLibraryItem,
    ) => {
      event.stopPropagation();
      const everShared = Boolean(model.firstSharedAt);
      const confirmed = window.confirm(
        everShared
          ? t("userModels3d.deleteSharedConfirm")
          : t("userModels3d.deletePrivateConfirm"),
      );
      if (!confirmed) return;
      const result = await deleteUserModel3D({ variables: { id: model.id } });
      if (result.data?.deleteUserModel3D === false) return;
      if (!everShared) {
        markUserModel3DUnavailable(model.id);
        clearDeletedUserModelReferences(model.id);
      }
      userModelsQuery.refetch();
    };

    const openEditUserModel = (
      event: React.MouseEvent,
      model: UserModel3DLibraryItem,
    ) => {
      event.stopPropagation();
      setEditingModel(model);
    };

    const handleEditedUserModel = (updated: UserModel3DLibraryItem) => {
      const hasPlacement = Array.from(
        floorplannerStore.symbolsMap.values(),
      ).some((symbol) => symbol.threeD?.userModel?.modelId === updated.id);
      if (hasPlacement) {
        floorplannerStore.beginUndoTransaction(
          "Update 3D object product details",
        );
        syncUserModel3DCommerceOnPlacements(
          floorplannerStore.symbolsMap,
          updated.id,
          commerceStampFromLibraryItem(updated),
          (symbolId, threeD) => {
            floorplannerStore.updateSymbolProperty(symbolId, "threeD", threeD);
          },
        );
        floorplannerStore.pushToUndoStack();
        invalidate();
      }
      void userModelsQuery.refetch();
    };

    const retryGeneration = async (
      event: React.MouseEvent,
      model: UserModel3DLibraryItem,
    ) => {
      event.stopPropagation();
      setModelError(null);
      setRetryingModelId(model.id);
      try {
        await retryUserModel3DGeneration({ variables: { id: model.id } });
        await userModelsQuery.refetch();
      } catch (error) {
        setModelError(
          error instanceof Error
            ? error.message
            : t("userModels3d.retryGenerationError"),
        );
      } finally {
        setRetryingModelId(null);
      }
    };

    const uploadPercent = uploadProgress
      ? Math.min(
          100,
          Math.max(
            0,
            uploadProgress.total > 0
              ? Math.round((uploadProgress.loaded / uploadProgress.total) * 100)
              : 0,
          ),
        )
      : 0;
    const displayUploadPercent =
      uploadProgress?.phase === "uploading"
        ? Math.min(50, Math.round(uploadPercent * 0.5))
        : uploadProgress?.phase === "cloud"
          ? 72
          : uploadProgress?.phase === "saving"
            ? 88
            : uploadProgress?.phase === "thumbnail"
              ? 96
              : 0;
    const uploadPhaseLabel =
      uploadProgress?.phase === "thumbnail"
        ? t("userModels3d.creatingThumbnail")
        : uploadProgress?.phase === "saving"
          ? t("userModels3d.addingToLibrary")
          : uploadProgress?.phase === "cloud"
            ? t("userModels3d.uploadingToStorage")
            : t("userModels3d.uploadingToServer");

    React.useEffect(() => {
      const updateViewportSize = () => setViewportSize(getViewportSize());

      window.addEventListener("resize", updateViewportSize);
      return () => window.removeEventListener("resize", updateViewportSize);
    }, []);

    const availableViewportHeight = Math.max(
      0,
      viewportSize.height - PANEL_VIEWPORT_GAP * 2,
    );
    const minimumVisibleHeight = Math.min(
      PANEL_MIN_VISIBLE_HEIGHT,
      availableViewportHeight,
    );
    const panelTop = Math.min(
      Math.max(PANEL_VIEWPORT_GAP, position?.top ?? PANEL_DEFAULT_TOP),
      Math.max(
        PANEL_VIEWPORT_GAP,
        viewportSize.height - PANEL_VIEWPORT_GAP - minimumVisibleHeight,
      ),
    );
    const panelWidth = Math.max(
      0,
      Math.min(PANEL_WIDTH, viewportSize.width - PANEL_VIEWPORT_GAP * 2),
    );
    const preferredLeft =
      position?.left ?? viewportSize.width - PANEL_DEFAULT_RIGHT - PANEL_WIDTH;
    const panelLeft = Math.min(
      Math.max(PANEL_VIEWPORT_GAP, preferredLeft),
      Math.max(
        PANEL_VIEWPORT_GAP,
        viewportSize.width - PANEL_VIEWPORT_GAP - panelWidth,
      ),
    );
    const panelMaxHeight = Math.max(
      0,
      viewportSize.height - panelTop - PANEL_VIEWPORT_GAP,
    );

    return (
      <div
        className="fixed z-50 overflow-y-auto rounded border border-gray-300 bg-white shadow-md scrollbar"
        style={{
          top: panelTop,
          left: panelLeft,
          width: panelWidth,
          maxHeight: panelMaxHeight,
        }}
        ref={dropdownRef}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between p-2">
          <div className="text-sm font-normal">
            {mode === "select" ? t("userModels3d.assignObject") : t("userModels3d.objects")}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-gray-500"
          >
            <IoClose style={{ strokeWidth: 0.5 }} size={24} />
          </button>
        </div>

        <div className="border-b border-gray-300" />

        <div className="space-y-3 p-3">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              className="flex min-h-[58px] items-center gap-2 rounded border border-[#dfe5e2] bg-[#f3f7f5] p-2.5 text-left hover:bg-[#e7efeb] focus:outline-none focus-visible:ring-2 focus-visible:ring-black disabled:cursor-wait disabled:opacity-60"
              disabled={uploadingModel}
              onClick={() => fileInputRef.current?.click()}
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-white">
                <FiUploadCloud size={17} />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium text-black">
                  {uploadingModel ? uploadPhaseLabel : t("userModels3d.addObject")}
                </span>
                <span className="block truncate text-[11px] text-gray-500">
                  {t("userModels3d.fileTypes")}
                </span>
              </span>
            </button>
            <button
              type="button"
              className="flex min-h-[58px] items-center gap-2 rounded border border-[#dfe5e2] bg-[#f3f7f5] p-2.5 text-left hover:bg-[#e7efeb] focus:outline-none focus-visible:ring-2 focus-visible:ring-black"
              onClick={() => setCreateFromImageOpen(true)}
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-white">
                <FiImage size={17} />
              </span>
              <span className="text-sm font-medium leading-4 text-black">
                {t("userModels3d.createFromImage")}
              </span>
            </button>
          </div>
          {selectedFile && !uploadingModel && (
            <div className="rounded border border-[#dfe5e2] bg-[#f3f7f5] p-3">
              <div className="flex items-center gap-3">
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded bg-white text-gray-600">
                  <FiBox size={24} />
                </span>
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-black">
                    {selectedFile.name}
                  </div>
                  <div className="mt-0.5 text-xs text-gray-500">
                    {formatBytes(selectedFile.size)}
                  </div>
                </div>
              </div>
              {canChoosePrivacy ? (
                <label className="mt-3 flex cursor-pointer items-start gap-2 border-t border-[#dfe5e2] pt-3 text-xs leading-relaxed text-gray-700">
                  <input
                    type="checkbox"
                    checked={shareWithCommunity}
                    onChange={(event) =>
                      setShareWithCommunity(event.target.checked)
                    }
                    className={`${BONI_CHECKBOX_CLASSNAME} mt-0.5`}
                  />
                  <span>{t("userModels3d.sharingConsent")}</span>
                </label>
              ) : (
                <p className="mt-3 border-t border-[#dfe5e2] pt-3 text-xs leading-relaxed text-gray-700">
                  {t("userModels3d.mandatorySharing")}
                </p>
              )}
              <UserModel3DCommerceFields
                compact
                value={commerceForm}
                onChange={setCommerceForm}
              />
              <div className="mt-3 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedFile(null);
                    setCommerceForm(emptyUserModel3DCommerceForm(floorplannerStore.currency));
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  className="rounded border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:border-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-black"
                >
                  {t("common.cancel")}
                </button>
                <button
                  type="button"
                  onClick={() => void uploadModel(selectedFile)}
                  className="rounded bg-boniYellowPrimary px-4 py-2 text-xs font-semibold text-black hover:brightness-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-black"
                >
                  {t("common.upload")}
                </button>
              </div>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".glb,.obj,.zip,.rar,model/gltf-binary,text/plain,application/zip,application/vnd.rar"
            className="hidden"
            onChange={(event) => {
              const file = event.currentTarget.files?.[0];
              if (file) chooseFile(file);
            }}
          />
          {uploadProgress && (
            <div className="rounded border border-[#dfe5e2] bg-white px-3 py-2">
              <div className="mb-1 flex items-center justify-between gap-3 text-xs text-gray-600">
                <span className="truncate">{uploadProgress.fileName}</span>
                <span className="shrink-0">
                  {uploadProgress.phase === "uploading"
                    ? `${formatBytes(uploadProgress.loaded)} / ${formatBytes(
                        uploadProgress.total,
                      )}`
                    : uploadPhaseLabel}
                </span>
              </div>
              <div className="h-1.5 overflow-hidden rounded bg-gray-100">
                <div
                  className={`h-full rounded bg-boniYellowPrimary transition-all ${
                    uploadProgress.phase === "uploading" ? "" : "animate-pulse"
                  }`}
                  style={{
                    width: `${displayUploadPercent}%`,
                  }}
                />
              </div>
              <div className="mt-1 text-right text-[11px] text-gray-500">
                {displayUploadPercent}%
              </div>
            </div>
          )}
          {modelError && (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {modelError}
            </div>
          )}

          <div>
            <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
              {t("userModels3d.my3dObjects")}
            </div>
            {userModelsQuery.loading && userModels.length === 0 ? (
              <div className="rounded border border-[#edebe6] px-3 py-4 text-center text-xs text-gray-500">
                {t("userModels3d.loadingObjects")}
              </div>
            ) : userModels.length === 0 ? (
              <div className="rounded border border-[#edebe6] px-3 py-4 text-center text-xs text-gray-500">
                {t("userModels3d.noObjects")}
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {userModels.map((model) => {
                  const modelTitle = getUserModel3DTitle(model);
                  const ready =
                    model.status === "ready" && Boolean(model.optimizedUrl);
                  const processingError = getReadableProcessingError(
                    model.errorMessage,
                  );
                  // Prefer the photo used for image→3D generation when present.
                  const thumbnailUrl = resolveUserModel3DUrl(
                    model.sourceImageUrl || model.thumbnailUrl,
                  );
                  const generationProgress = Math.round(
                    Math.max(0, Math.min(1, model.progress ?? 0)) * 100,
                  );
                  const stageLabel =
                    model.stage === "submitting"
                      ? t("userModels3d.starting")
                      : model.stage === "generating"
                        ? t("userModels3d.generating")
                        : model.stage === "downloading"
                          ? t("userModels3d.downloading")
                          : model.stage === "optimizing"
                            ? t("userModels3d.optimizing")
                            : t("userModels3d.queued");
                  return (
                    <div
                      key={model.id}
                      role="button"
                      tabIndex={ready ? 0 : -1}
                      className={`group relative h-[96px] rounded border bg-white text-left ${
                        selectedModelId === model.id
                          ? "border-gray-700 ring-1 ring-gray-700"
                          : ready
                            ? "cursor-pointer border-[#dfe5e2] hover:border-boniYellowPrimary"
                            : "cursor-default border-[#edebe6]"
                      }`}
                      onClick={() => handleReadyModel(model)}
                      onKeyDown={(event) => {
                        if (
                          (event.key === "Enter" || event.key === " ") &&
                          ready
                        ) {
                          event.preventDefault();
                          handleReadyModel(model);
                        }
                      }}
                    >
                      {/** Clip media only — keep the card overflow-visible so tooltips are not cut off. */}
                      <div className="absolute inset-0 overflow-hidden rounded">
                        {thumbnailUrl ? (
                          <img
                            src={thumbnailUrl}
                            alt={modelTitle}
                            className="h-full w-full object-contain p-2"
                            crossOrigin="use-credentials"
                          />
                        ) : (
                          <span className="flex h-full w-full items-center justify-center bg-[#f7f6f0] text-gray-400">
                            <FiBox size={22} />
                          </span>
                        )}
                        <div className="absolute inset-x-0 bottom-0 bg-black/60 px-2 py-1 text-[10px] leading-3 text-white">
                          <span className="block truncate">{modelTitle}</span>
                          {!ready && (
                            <span className="block truncate text-white/90">
                              {model.status === "processing"
                                ? model.source === "image"
                                  ? `${stageLabel} · ${generationProgress}%`
                                  : t("userModels3d.processing")
                                : t("userModels3d.failed")}
                            </span>
                          )}
                        </div>
                        {ready && (
                          <span
                            className={`absolute bottom-7 right-1 rounded px-1.5 py-0.5 text-[9px] font-medium ${
                              model.isCommunityListed
                                ? "bg-boniYellowPrimary text-black"
                                : "bg-white/90 text-gray-700"
                            }`}
                          >
                            {model.isCommunityListed
                              ? t("userModels3d.shared")
                              : t("userModels3d.private")}
                          </span>
                        )}
                        {model.status === "processing" &&
                          model.source === "image" && (
                            <span className="absolute inset-x-2 bottom-10 h-1 overflow-hidden rounded bg-white/70">
                              <span
                                className="block h-full rounded bg-boniYellowPrimary transition-[width]"
                                style={{ width: `${generationProgress}%` }}
                              />
                            </span>
                          )}
                        {model.status === "failed" && (
                          <p
                            className="absolute inset-x-1 top-9 line-clamp-2 rounded bg-red-50/95 px-1.5 py-1 text-[9px] leading-3 text-red-800"
                            title={processingError}
                          >
                            {processingError}
                          </p>
                        )}
                      </div>
                      <div className="absolute left-1 top-1 z-20 hidden group-hover:block">
                        <Tooltip
                          content={t("userModels3d.editTooltip")}
                          placement="top"
                        >
                          <button
                            type="button"
                            className="flex h-7 w-7 items-center justify-center rounded bg-white text-gray-700 shadow"
                            onClick={(event) => openEditUserModel(event, model)}
                            aria-label={t("userModels3d.editAriaLabel", {
                              name: modelTitle,
                            })}
                          >
                            <FiEdit2 size={13} />
                          </button>
                        </Tooltip>
                      </div>
                      {model.status === "failed" &&
                        model.source === "image" && (
                          <button
                            type="button"
                            disabled={retryingModelId === model.id}
                            className="absolute left-1 top-1 flex h-7 items-center gap-1 rounded bg-white px-2 text-[10px] font-semibold text-gray-800 shadow disabled:opacity-50"
                            onClick={(event) =>
                              void retryGeneration(event, model)
                            }
                            aria-label={t("userModels3d.retryAriaLabel", {
                              name: modelTitle,
                            })}
                          >
                            <FiRefreshCw size={11} /> {t("common.retry")}
                          </button>
                        )}
                      {!(
                        model.source === "image" &&
                        model.status === "processing"
                      ) && (
                        <button
                          type="button"
                          className={`absolute right-1 top-1 h-7 w-7 items-center justify-center rounded bg-white text-red-600 shadow ${
                            model.status === "failed"
                              ? "flex"
                              : "hidden group-hover:flex"
                          }`}
                          onClick={(event) => deleteUserModel(event, model)}
                          aria-label={t("userModels3d.deleteAriaLabel", {
                            name: modelTitle,
                          })}
                        >
                          <FiTrash2 size={14} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        {createFromImageOpen && (
          <CreateModel3DFromImageModal
            onClose={() => setCreateFromImageOpen(false)}
            onGenerated={() => void userModelsQuery.refetch()}
          />
        )}
        {editingModel && (
          <EditUserModel3DModal
            model={editingModel}
            onClose={() => setEditingModel(null)}
            onSaved={handleEditedUserModel}
          />
        )}
      </div>
    );
  },
);
